import type { ThreeEvent } from '@react-three/fiber';
import { useSceneStore } from '../store/useSceneStore';

/**
 * Shared onClick handler for any selectable mesh in the scene.
 *
 * Plan 04-03 Task 2 — raycast selection via R3F's auto-raycaster.
 *
 * Closures:
 *   - Pitfall #3 (select-through): `event.stopPropagation()` is the FIRST line.
 *     Without it, a click that hits the pavilion also bubbles to the ground
 *     plane underneath, and whichever onClick fires last wins — in practice,
 *     that's the ground, so the Sources panel would show "ground" instead of
 *     the object the user meant to click.
 *   - Pitfall #10 (pointer event storm): this handler is ONLY attached to
 *     meshes that pass `PlaceholderScene.isSelectable()` — scene chrome
 *     (ground, walls) never carries onClick at all, so R3F doesn't ray-test
 *     them on every pointer move.
 *
 * Defensive non-string bailout: userData.manifestId should always be a
 * non-empty string (enforced by schema.ts + PlaceholderObject), but if an
 * author stuffs a number or blank string in Blender custom props we fail
 * silently rather than fabricate a selected id — Pitfall #5 style defense.
 */
export function onMeshClick(event: ThreeEvent<MouseEvent>): void {
  event.stopPropagation();
  const manifestId = event.object.userData?.manifestId;
  if (typeof manifestId !== 'string' || manifestId.length === 0) return;
  useSceneStore.getState().setSelectedObjectId(manifestId);
}
